// Added by Sejune Oh on 2020-07-07
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import RootStore from '../../index'

import { SiteConfig, SiteConfigOption } from '@/helper/define/siteConfigDefine'
import { reqGetSiteConfig, reqGetDSSiteConfig, reqUpdateSiteConfg, reqUpdateDSSiteConfig, reqDirPathCheck, reqResetDSSiteConfig } from '@/service/siteConfig.service'
import Logger from '@/helper/Logger'

interface SiteConfigStoreInterface {
  getAllConfigs(force: boolean): Promise<SiteConfigOption | undefined>
  getDSConfigs(dsToken: string): Promise<SiteConfigOption | undefined>
  updateConfigOptions(configOptions: SiteConfig[]): Promise<boolean>
  updateDSConfigOptions(configOptions: SiteConfig[], dsToken: string): Promise<boolean>
  getSiteConfigByVariable(configKey: string, strictTypeRequired: boolean): undefined | string | boolean | number
  GetSiteConfigByKey(configKey: string): string | undefined
}

export default class SiteConfigStore implements SiteConfigStoreInterface {
  private _siteConfigOption: SiteConfigOption
  private _dsConfigOption: SiteConfigOption
  private _isInitialized: boolean
  _storeName: string
  _root: RootStore

  constructor(root: RootStore) {
    this._storeName = 'site'
    this._root = root
    this._siteConfigOption = {}
    this._dsConfigOption = {}
    this._isInitialized = false
  }

  get storeName() {
    return this._storeName
  }

  get siteConfigOption () {
    return this._siteConfigOption
  }

  get dsConfigOption () {
    return this._dsConfigOption
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * Client에 저장되어있는 config 값을 업데이트합니다.
   * @param {SiteConfig[]} configOptions
   */
  private _updateCachedSiteConfig(configOptions: SiteConfig[]): void {
    for (const configOption of configOptions) {
      const [group, optionKey] = configOption.key.split('.')

      if (!this._siteConfigOption[group]) {
        this._siteConfigOption[group] = {}
      }

      if (!this._siteConfigOption[group][optionKey]) {
        this._siteConfigOption[group][optionKey] = configOption
      } else {
        this._siteConfigOption[group][optionKey] = {
          ...this._siteConfigOption[group][optionKey],
          value: configOption.value,
        }
      }
    }
  }

  private _updateDSConfig(configOptions: SiteConfig[]): void {
    for (const configOption of configOptions) {
      const [group, optionKey] = configOption.key.split('.')

      if (!this._dsConfigOption[group]) {
        this._dsConfigOption[group] = {}
      }

      if (!this._dsConfigOption[group][optionKey]) {
        this._dsConfigOption[group][optionKey] = configOption
      } else {
        this._dsConfigOption[group][optionKey] = {
          ...this._dsConfigOption[group][optionKey],
          value: configOption.value,
          default_value: configOption.default_value,
          ds_overrided: configOption.ds_overrided,
        }
      }
    }

  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * Store를 초기화 합니다.
   */
  public async initialize(force = false) {
    if (this._isInitialized && !force) {
      Logger.infoLog('SiteConfigStore가 이미 초기화되어있는 상태입니다.')
      return
    }

    const siteConfigOption = await this.getAllConfigs(true)

    if (!siteConfigOption) {
      Logger.errLog('SiteConfigStore를 초기화하는데 실패하였습니다.')
      return
    }

    this._siteConfigOption = siteConfigOption
    this._isInitialized = true
  }

  /**
   * 모든 Config 정보를 조회합니다
   * @param {boolean} [force=false] 데이터 존재 여부와 관계없이 서버에서 새로 데이터를 받아올 수 있도록 하는 플래그
   */
  public async getAllConfigs(force = false): Promise<SiteConfigOption | undefined> {
    if (this._isInitialized && !force) return this._siteConfigOption

    try {
      const { data: { items } } = await reqGetSiteConfig()
      this._updateCachedSiteConfig(items)
      return this._siteConfigOption
    } catch (e) {
      Logger.errLog('Failed to update site config options.')
      console.error(e)
      return undefined
    }
  }

  /**
   * 데이터 스토리지 토큰을 통해서 가져오기
   * @param dsToken
   */
  public async getDSConfigs(dsToken: string): Promise<SiteConfigOption | undefined> {
    try {
      const { data: { items } } = await reqGetDSSiteConfig(dsToken)
      this.refreshDsConfigOption(items)
      return this._dsConfigOption
    } catch (e) {
      Logger.errLog('Failed to get ds site config options.')
      console.error(e)
      return undefined
    }
  }

  /**
   * Site config를 업데이트합니다.
   * @param {SiteConfig[]} configOptions
   */
  public async updateConfigOptions(configOptions: SiteConfig[]): Promise<boolean> {
    try {
      await reqUpdateSiteConfg(configOptions)
      this._updateCachedSiteConfig(configOptions)
      return true
    } catch (e) {
      Logger.errLog('Failed to update site config options.')
      console.error(e)
      return false
    }
  }

  /**
   * Data storage를 업데이트합니다.
   * @param configOptions
   * @param dsToken
   */
  public async updateDSConfigOptions(configOptions: SiteConfig[], dsToken: string): Promise<boolean> {
    try {
      const { data: { items } } = await reqUpdateDSSiteConfig(configOptions, dsToken)
      this.refreshDsConfigOption(items)
      return true
    } catch (e) {
      Logger.errLog('Failed to update ds site config options.')
      console.error(e)
      return false
    }
  }

  /**
   * Data storage config를 필터링하고 갱신함
   * @param configOptions
   */
  private refreshDsConfigOption = (configOptions: SiteConfig[]) => {
    // filter 걸기
    const filteredItems = configOptions.filter(configs => configs.overridable_on_ds)
    this._updateDSConfig(filteredItems)
  }

  /**
   * LegacyApp에서 SiteConfig가 있는지 검증하는 메소드
   * @param {string} configKey
   * @param {boolean} [strictTypeRequired=false] type 명세에 따른 형변환 여부를 확인하는 플래그
   */
  public getSiteConfigByVariable(configKey: string, strictTypeRequired = false): undefined | string | boolean | number {
    const [group, optionKey] = configKey.split('.')
    if (!this._siteConfigOption[group] || !this._siteConfigOption[group][optionKey]) return undefined

    const matchedConfig = this._siteConfigOption[group][optionKey]
    if (!strictTypeRequired) return matchedConfig.value

    switch (matchedConfig.type) {
      case 'bool':
        return matchedConfig.value === 'true'
      case 'enum':
      case 'string':
        return matchedConfig.value
      case 'int':
        return parseInt(matchedConfig.value)
      case 'real':
        return parseFloat(matchedConfig.value)
      default:
        return undefined
    }
  }

  /**
   * Site config 값을 찾는다.
   * find_config.pl 에서 찾을 수 있도록 React code 상에서는 이 메소드를 사용할 것.
   * @param {string} configKey
   */
  public GetSiteConfigByKey(configKey: string): string | undefined {
    return this.getSiteConfigByVariable(configKey) as string | undefined
  }

  /** Cuvis Export Path를 검증한다 */
  public getPathCheck = async (filePath: string) => {
    const { data } = await reqDirPathCheck(filePath)
    return data
  }

  /**
   * storage config값을 삭제함
   */
  public async resetDSConfig(dsToken: string): Promise<boolean> {
    try {
      await reqResetDSSiteConfig(dsToken)
      await this.getDSConfigs(dsToken)
      return true
    } catch (e) {
      Logger.errLog('Failed to reset ds site config options.')
      console.error(e)
      return false
    }
  }
}
